import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import BreakthroughSessionBackground, { getBreakthroughTheme } from '../components/portal/BreakthroughSessionBackground';
import PortalBackground from '../components/PortalBackground';
import { Calendar, DollarSign, TrendingUp, X, AlertTriangle, Check, Trash2, Upload, Paperclip, MessageSquare, Star, Clock, ChevronRight, Video, CalendarClock, ExternalLink } from 'lucide-react';
import {
  normEmail,
  pickProfilePicUrl,
  accountDisplayForCheckIn,
  checkInDateToJs,
} from '../components/crm/CheckInInsights';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Legend, Area } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { ClipboardIcon } from 'lucide-react';
import {
  getAccountsByTypeAndDateRange,
  getAccounts,
  getAccountsByType,
  findAccountIdByEmail,
  getAccountById,
  getCrmContactDisplayPhotoUrl,
} from '../services/firestoreService';
import { loadAccountMeetingSummaries, matchEventToMeetingSummary, normalizeMeetingTitle } from '../utils/meetingSummaryLookup';
import { parseFathomSummarySections } from '../utils/fathomSummaryParser';
import { matchesDepartmentTitle } from '../services/departmentCallsService';
import { getFiresAssignedToCurrentUser } from '../services/firebase/fires';
import { getOpenQuestionsByAssignee } from '../services/firebase/questions';
import { getTasksByUser, toggleTaskCompletion, addTaskComment } from '../services/taskService';
import { getEventsByUser, getEventsByTimeframe } from '../services/eventService';
import { fetchGoogleCalendarEvents } from '../services/googleCalendarService';
import { getUserIntegrationsStatus } from '../services/userIntegrationService';
import { getCurrentUserActivities } from '../services/activityService';
import { getRecentAnnouncements, createAnnouncement, getAnnouncementsWithSeenStatus, deleteAnnouncement, markAnnouncementAsSeen, getUnseenAnnouncementsCount, uploadAnnouncementFile } from '../services/firebase/announcements';
import { subscribeToTotalUnreadCount } from '../services/messagingService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import Modal from '../components/Modal';
import { getInternalUsers } from '../services/userService';
import { getTaskComments } from '../services/taskService';
import { db } from '../firebase'; // Added missing import for db
import { doc, getDoc, collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore'; // Added missing imports
import { CRM_OWNER_EXECUTIVE_ROLES } from '../services/accessControlService';
import HRFulfillmentWidgets from './crm/HRDashboard';
import MarketingFulfillmentWidgets from './crm/MarketingDashboard';
import { isActiveClientAccount as isActiveClientLifecycle } from '../utils/accountLifecycle';
import {
  convertToDate as convertToDateUtil,
  filterTasksByTimeframe as filterTasksByTimeframeUtil,
  filterEventsByTimeframe as filterEventsByTimeframeUtil,
} from '../utils/dashboardFilters';

const monthLabelForCalendarMonth = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

// Strip the "9:00 AM - " prefix Google events carry in their title.
const cleanEventTitle = (title) =>
  String(title || '').replace(/^\d{1,2}:\d{2}\s?(AM|PM)\s+-\s+/i, '').trim() || 'Untitled call';

// Friendly relative day label for upcoming client calls: "Today", "Tomorrow",
// "this Wednesday" for other days still in the current week, otherwise the full
// "Wednesday, July 16" date.
const formatCallDayLabel = (date) => {
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((target - startOfToday) / dayMs);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  // Start of next week (Monday). getDay(): 0=Sun … 6=Sat.
  const daysUntilNextMonday = (8 - now.getDay()) % 7 || 7;
  const startOfNextWeek = new Date(startOfToday.getTime() + daysUntilNextMonday * dayMs);
  if (target < startOfNextWeek) {
    return `this ${date.toLocaleDateString(undefined, { weekday: 'long' })}`;
  }
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
};

// Match a calendar event to a client account: attendee email first, then
// company name appearing in the event title as a fallback for CRM events.
const matchEventToClientAccount = (event, accounts) => {
  const attendeeEmails = (event.attendees || [])
    .map((a) => String(a?.email || '').trim().toLowerCase())
    .filter(Boolean);
  if (attendeeEmails.length > 0) {
    const byEmail = accounts.find((acc) => {
      const accEmail = String(acc.contact?.email || '').trim().toLowerCase();
      return accEmail && attendeeEmails.includes(accEmail);
    });
    if (byEmail) return byEmail;
  }
  const title = String(event.title || '').toLowerCase();
  if (!title) return null;
  return (
    accounts.find((acc) => {
      const company = String(acc.company || '').trim().toLowerCase();
      return company.length > 2 && title.includes(company);
    }) || null
  );
};

// Agenda rows the client filled in when booking on a department page. They arrive as
// "45 min — Focus" lines inside the Calendly-created Google event description.
const parseAgendaFromDescription = (description) => {
  if (!description) return [];
  const text = String(description)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => /^\d+\s*min(?:ute)?s?\s*[—–-]/i.test(line));
};

const DEBRIEF_DEPARTMENTS = ['sales', 'operations', 'finance', 'technology', 'hr', 'marketing'];
const DEBRIEF_DEPARTMENT_LABELS = {
  sales: 'Sales',
  operations: 'Operations',
  finance: 'Finance',
  technology: 'Technology',
  hr: 'HR',
  marketing: 'Marketing',
};

// Leads & Clients chart (Owner / Executive only) — replaces the old Department
// Updates panel. Timeframe options and how many trailing months each covers;
// YTD is computed from January of the current year.
const LEADS_CLIENTS_RANGES = [
  { key: 'month', label: 'Current Month' },
  { key: '3m', label: 'Last 3 Months' },
  { key: '6m', label: 'Last 6 Months' },
  { key: 'ytd', label: 'YTD' },
];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// When a lead entered the pipeline (persists after conversion to client).
const getLeadReceivedDate = (account) =>
  checkInDateToJs(account.receivedDate || account.createdAt);

// When an account became a client: explicit timestamps, then the
// "Converted to Client" status history entry, then contract start / created.
// Mirrors getClientSignedDate in SalesReports.
const getClientSignedDate = (account) => {
  if (account.wonAt) return checkInDateToJs(account.wonAt);
  if (account.convertedAt) return checkInDateToJs(account.convertedAt);
  const conversion = (account.statusHistory || []).find(
    (entry) => typeof entry.action === 'string' && entry.action.startsWith('Converted to Client')
  );
  const conversionDate = conversion ? checkInDateToJs(conversion.date) : null;
  return conversionDate || checkInDateToJs(account.contractStart || account.createdAt);
};

// Same "active client" filter the New Clients stat uses.
const isActiveClientAccount = (client) => isActiveClientLifecycle(client);

// One-line takeaway from a Fathom meeting summary (purpose first, then key takeaways).
const briefFathomSummary = (summaryDoc) => {
  const sections = parseFathomSummarySections(summaryDoc?.summary || '');
  const source = sections.meetingPurpose || sections.keyTakeaways || summaryDoc?.summary || '';
  return (
    String(source)
      .split('\n')
      .map((line) => line.replace(/^[-*•]\s*/, '').replace(/[*_#`]/g, '').trim())
      .filter(Boolean)[0] || ''
  );
};

const emptyCheckInStats = {
  totalRows: 0,
  pending: 0,
  responded: 0,
  ratingsCount: 0,
  avgRating: null,
  distribution: [0, 0, 0, 0, 0],
  barChartRows: [],
  pendingSubmitters: [],
};

async function fetchCheckInsStatsForMonth(year, month) {
  const periodKey = `${year}-${String(month).padStart(2, '0')}`;
  const monthLabel = monthLabelForCalendarMonth(year, month);
  try {
    const checkInsRef = collection(db, 'check_ins');
    const [byPeriod, byMonthLabel] = await Promise.all([
      getDocs(query(checkInsRef, where('periodKey', '==', periodKey))),
      getDocs(query(checkInsRef, where('month', '==', monthLabel))),
    ]);
    const merged = new Map();
    for (const d of byPeriod.docs) merged.set(d.id, d.data());
    for (const d of byMonthLabel.docs) merged.set(d.id, d.data());

    const docs = [...merged.values()];
    let pending = 0;
    let responded = 0;
    const pendingEntries = []; // { accountId, email, date } for the Pending card hover
    const distribution = [0, 0, 0, 0, 0];
    let sumRating = 0;
    let countRating = 0;
    const bucketSeen = [new Set(), new Set(), new Set(), new Set(), new Set()];
    const bucketEmails = [[], [], [], [], []];
    // Per-bucket, per-email follow-up info: comment text + whether a check-in call was requested.
    const bucketMeta = [new Map(), new Map(), new Map(), new Map(), new Map()];
    const mergeMeta = (idx, normEmailKey, comment, requestedCall, date) => {
      if (!normEmailKey) return;
      const prev = bucketMeta[idx].get(normEmailKey) || { comment: '', requestedCall: false, date: null };
      // Keep the most recent submission date for this responder.
      const nextDate =
        date && (!prev.date || date.getTime() > prev.date.getTime()) ? date : prev.date;
      bucketMeta[idx].set(normEmailKey, {
        comment: prev.comment || comment || '',
        requestedCall: prev.requestedCall || !!requestedCall,
        date: nextDate,
      });
    };

    const resolveEmailFromUserId = async (uid) => {
      if (!uid) return '';
      try {
        const u = await getDoc(doc(db, 'users', uid));
        const data = u.data();
        if (data?.email) return String(data.email).trim();
        if (data?.accountId) {
          const acc = await getAccountById(data.accountId);
          return String(acc?.contact?.email || '').trim();
        }
      } catch {
        /* ignore */
      }
      return '';
    };

    for (const d of docs) {
      const rawSat = d.overallSatisfaction ?? d.rating;
      const num = Number(rawSat);
      const hasNumeric = Number.isFinite(num) && num >= 1 && num <= 5;
      const statusOk =
        typeof d.overallStatus === 'string' &&
        ['Good', 'Okay', 'Not Good'].includes(d.overallStatus);
      const isResponded = hasNumeric || d.status === 'completed' || statusOk;
      const isPending = !isResponded && (d.status === 'pending' || !!d.surveyId);

      if (isResponded) {
        responded++;
        if (hasNumeric) {
          sumRating += num;
          countRating++;
          const idx = Math.min(4, Math.max(0, Math.round(num) - 1));
          distribution[idx] += 1;
          const emailRaw = String(d.userEmail || '').trim();
          const n = normEmail(emailRaw);
          if (n && !bucketSeen[idx].has(n)) {
            bucketSeen[idx].add(n);
            bucketEmails[idx].push(emailRaw);
          }
          const submittedAt = checkInDateToJs(d.submittedAt || d.completedAt || d.createdAt);
          mergeMeta(idx, n, String(d.comments || d.additionalFeedback || '').trim(), d.requestedCheckInCall === true, submittedAt);
        }
      } else if (isPending) {
        pending++;
        pendingEntries.push({
          accountId: d.accountId || null,
          email: String(d.userEmail || '').trim(),
          date: checkInDateToJs(d.scheduledDate || d.createdAt),
        });
      }
    }

    const withRatingNoEmail = [];
    for (const d of docs) {
      const rawSat = d.overallSatisfaction ?? d.rating;
      const num = Number(rawSat);
      const hasNumeric = Number.isFinite(num) && num >= 1 && num <= 5;
      if (!hasNumeric || !d.userId) continue;
      if (normEmail(d.userEmail)) continue;
      const idx = Math.min(4, Math.max(0, Math.round(num) - 1));
      withRatingNoEmail.push({
        idx,
        userId: d.userId,
        comment: String(d.comments || d.additionalFeedback || '').trim(),
        requestedCall: d.requestedCheckInCall === true,
        date: checkInDateToJs(d.submittedAt || d.completedAt || d.createdAt),
      });
    }
    await Promise.all(
      withRatingNoEmail.map(async ({ idx, userId, comment, requestedCall, date }) => {
        const email = await resolveEmailFromUserId(userId);
        const n = normEmail(email);
        if (!n) return;
        if (!bucketSeen[idx].has(n)) {
          bucketSeen[idx].add(n);
          bucketEmails[idx].push(email);
        }
        mergeMeta(idx, n, comment, requestedCall, date);
      })
    );

    const normToCanonical = new Map();
    for (let i = 0; i < 5; i++) {
      for (const em of bucketEmails[i]) {
        const n = normEmail(em);
        if (n && !normToCanonical.has(n)) normToCanonical.set(n, em);
      }
    }

    const emailDisplay = {};
    await Promise.all(
      [...normToCanonical.keys()].map(async (n) => {
        const canonical = normToCanonical.get(n);
        try {
          const accountId = await findAccountIdByEmail(canonical);
          if (!accountId) {
            emailDisplay[n] = { name: canonical, imageUrl: '', accountId: null };
            return;
          }
          const account = await getAccountById(accountId);
          emailDisplay[n] = { ...accountDisplayForCheckIn(account, canonical), accountId };
        } catch {
          emailDisplay[n] = { name: canonical, imageUrl: '', accountId: null };
        }
      })
    );

    const barChartRows = [0, 1, 2, 3, 4].map((i) => ({
      stars: `${i + 1}`,
      count: distribution[i],
      submitters: bucketEmails[i].map((em) => {
        const n = normEmail(em);
        const meta = bucketMeta[i].get(n) || { comment: '', requestedCall: false, date: null };
        return { ...emailDisplay[n], comment: meta.comment, requestedCall: meta.requestedCall, date: meta.date };
      }),
    }));

    // Resolve who is still pending (by accountId, falling back to email) for the Pending card hover.
    const pendingResolved = await Promise.all(
      pendingEntries.map(async (e) => {
        try {
          let accountId = e.accountId;
          if (!accountId && e.email) accountId = await findAccountIdByEmail(e.email);
          if (accountId) {
            const account = await getAccountById(accountId);
            return { ...accountDisplayForCheckIn(account, e.email), accountId, date: e.date };
          }
        } catch {
          /* ignore */
        }
        return { name: e.email || 'Pending client', contactName: '', imageUrl: '', accountId: null, date: e.date };
      })
    );
    // Dedupe in case a client has more than one pending doc this month; keep the earliest scheduled date.
    const pendingByKey = new Map();
    for (const p of pendingResolved) {
      const key = p.accountId || p.name;
      const prev = pendingByKey.get(key);
      if (!prev) {
        pendingByKey.set(key, p);
      } else if (p.date && (!prev.date || p.date.getTime() < prev.date.getTime())) {
        pendingByKey.set(key, { ...prev, date: p.date });
      }
    }
    const pendingSubmitters = [...pendingByKey.values()];

    return {
      totalRows: docs.length,
      pending,
      responded,
      ratingsCount: countRating,
      avgRating: countRating ? sumRating / countRating : null,
      distribution,
      barChartRows,
      pendingSubmitters,
    };
  } catch (error) {
    console.error('Error fetching check-ins for dashboard:', error);
    return { ...emptyCheckInStats };
  }
}

// Add shine effect styles
const shineStyles = `
  @keyframes shine {
    0% {
      background-position: -200% center;
    }
    100% {
      background-position: 200% center;
    }
  }

  .metrics-shine {
    position: relative;
    overflow: hidden;
  }

  .metrics-shine::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: skewX(-25deg);
    transition: all 0.75s ease;
  }

  .metrics-shine:hover::after {
    left: 150%;
    transition: all 0.75s ease;
  }

  .hover-lift {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .hover-lift:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  @keyframes galleryFade {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .dashboard-gallery-fade {
    animation: galleryFade 0.5s ease;
  }
`;

const Dashboard = () => {
  console.log('=== DASHBOARD COMPONENT RENDERING ===');
  
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  
  // Immediate debug when component mounts
  console.log('Dashboard component mounted');
  
  // State for dashboard metrics
  const [dashboardMetrics, setDashboardMetrics] = useState({
    newLeads: 0,
    newClients: 0,
    questions: 0,
    fires: 0
  });
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkInStats, setCheckInStats] = useState(() => ({ ...emptyCheckInStats }));
  const [currentDate, setCurrentDate] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  
  // State for user tasks
  const [userTasks, setUserTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  
  // State for user events
  const [userEvents, setUserEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] = useState(false);

  // State for Client Calls + company debrief
  const [clientAccounts, setClientAccounts] = useState([]);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [debriefCheckIn, setDebriefCheckIn] = useState(null);
  const [accountSummaries, setAccountSummaries] = useState([]);
  const [debriefLoading, setDebriefLoading] = useState(false);
  
  // State for breakthrough sessions
  const [thisWeekSessions, setThisWeekSessions] = useState([]);
  const [nextWeekSessions, setNextWeekSessions] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // Rotating gallery (Breakthrough Sessions · Client Check-Ins)
  const [activePanel, setActivePanel] = useState(0);
  const [galleryPaused, setGalleryPaused] = useState(false);
  // Leads & Clients chart (Owner / Executive only) — raw lead + client accounts
  // and the selected timeframe. Monthly series is derived in a memo below.
  const [leadsClientsRange, setLeadsClientsRange] = useState('3m');
  const [leadsClientsAccounts, setLeadsClientsAccounts] = useState(null);
  const [leadsClientsLoading, setLeadsClientsLoading] = useState(false);
  
  // State for dropdowns and selections
  const [tasksDropdownOpen, setTasksDropdownOpen] = useState(false);
  const [eventsDropdownOpen, setEventsDropdownOpen] = useState(false);
  const [selectedTaskPeriod, setSelectedTaskPeriod] = useState('This Week');
  const [selectedEventPeriod, setSelectedEventPeriod] = useState('This Week');
  
  // Helper functions for localStorage persistence
  const getStorageKey = (key) => {
    return currentUser ? `dashboard_${key}_${currentUser.uid}` : null;
  };
  
  const loadUserPreferences = () => {
    if (!currentUser) return;
    
    try {
      const taskPeriodKey = getStorageKey('taskPeriod');
      const eventPeriodKey = getStorageKey('eventPeriod');
      
      if (taskPeriodKey) {
        const savedTaskPeriod = localStorage.getItem(taskPeriodKey);
        if (savedTaskPeriod && ['Today', 'This Week', 'This Month'].includes(savedTaskPeriod)) {
          setSelectedTaskPeriod(savedTaskPeriod);
        }
      }
      
      if (eventPeriodKey) {
        const savedEventPeriod = localStorage.getItem(eventPeriodKey);
        if (savedEventPeriod && ['Today', 'This Week', 'This Month'].includes(savedEventPeriod)) {
          setSelectedEventPeriod(savedEventPeriod);
        }
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };
  
  const saveTaskPeriodPreference = (period) => {
    if (!currentUser) return;
    
    try {
      const key = getStorageKey('taskPeriod');
      if (key) {
        localStorage.setItem(key, period);
      }
    } catch (error) {
      console.error('Error saving task period preference:', error);
    }
  };
  
  const saveEventPeriodPreference = (period) => {
    if (!currentUser) return;
    
    try {
      const key = getStorageKey('eventPeriod');
      if (key) {
        localStorage.setItem(key, period);
      }
    } catch (error) {
      console.error('Error saving event period preference:', error);
    }
  };
  const [selectedGoal, setSelectedGoal] = useState('leads');
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState('');
  const [showNewAnnouncementForm, setShowNewAnnouncementForm] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  
  // Add state for announcement type selection
  const [announcementType, setAnnouncementType] = useState('company'); // 'company' or 'client'
  
  // Add state to track user type
  const [userType, setUserType] = useState(null);
  /** Role from internal_users (CRM); used for Owner/Executive-only navigation. */
  const [internalCrmRole, setInternalCrmRole] = useState(null);
  /** Team from internal_users; HR team gets the dedicated HR dashboard. */
  const [userTeam, setUserTeam] = useState(null);
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploadError, setFileUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  // Add state for Task/Event Details Modal
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskOrEvent, setSelectedTaskOrEvent] = useState(null);
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);
  const [taskDetailsError, setTaskDetailsError] = useState(null);
  const [taskDetailsComments, setTaskDetailsComments] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // Past calendar events across ALL internal users (for client "Last Calls"), each tagged with ownerUid.
  const [allInternalEvents, setAllInternalEvents] = useState([]);
  
  // New comment state
  const [newTaskComment, setNewTaskComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [commentFile, setCommentFile] = useState(null);
  const [commentFileError, setCommentFileError] = useState('');
  
  // Fetch dashboard metrics
  useEffect(() => {
    const fetchDashboardMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching dashboard metrics...');
        
        const [newLeads, newClients, userFires, openQuestions, checkInStatsResult] = await Promise.all([
          getAccountsByTypeAndDateRange('Leads', 14),
          getAccountsByTypeAndDateRange('Clients', 14),
          getFiresAssignedToCurrentUser(),
          getOpenQuestionsByAssignee(currentUser?.uid), // Use actual current user ID
          fetchCheckInsStatsForMonth(currentDate.year, currentDate.month)
        ]);
        
        // Filter leads: only count those with showOnKanban !== false (defaults to true if not set)
        const filteredLeads = newLeads.filter(lead => lead.showOnKanban !== false);
        
        // Filter clients: only count active clients
        // Active clients are those that are:
        // - Not Lost (status !== 'Lost' and program?.clientStatus !== 'Lost')
        // - Not Archived (stage !== 'Archived')
        // - Not deleted (isDeleted !== true, deleted !== true, status !== 'Deleted')
        const filteredClients = newClients.filter((client) => isActiveClientAccount(client));
        
        console.log('New leads fetched:', newLeads);
        console.log('Filtered leads (showOnKanban !== false):', filteredLeads);
        console.log('New leads count:', filteredLeads.length);
        
        // Fetch new clients (accounts created in last 30 days with type 'Clients')
        console.log('New clients fetched:', newClients);
        console.log('Filtered active clients:', filteredClients);
        console.log('New clients count:', filteredClients.length);
        
        // Fetch fires assigned to current user
        console.log('User fires fetched:', userFires);
        console.log('User fires count:', userFires.length);
        
        // Fetch open questions assigned to current user
        console.log('Open questions fetched:', openQuestions);
        console.log('Open questions count:', openQuestions.length);

        setCheckInStats(checkInStatsResult);

        setDashboardMetrics(prev => ({
          ...prev,
          newLeads: filteredLeads.length,
          newClients: filteredClients.length,
          fires: userFires.length,
          questions: openQuestions.length
        }));

      } catch (err) {
        console.error('Error fetching dashboard metrics:', err);
        setError('Failed to fetch dashboard metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardMetrics();
  }, [currentDate, currentUser]);

  // Fetch user tasks
  const fetchUserTasks = async () => {
    if (!currentUser) {
      console.log('No current user, skipping task fetch');
      return;
    }
    
    try {
      setTasksLoading(true);
      console.log('Fetching tasks for user:', currentUser.uid);
      const tasks = await getTasksByUser(currentUser.uid);
      console.log('Tasks fetched:', tasks);
      console.log('Tasks count:', tasks.length);
      setUserTasks(tasks);
    } catch (err) {
      console.error('Error fetching user tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  };

  // Fetch user events
  const fetchUserEvents = async () => {
    if (!currentUser) return;
    
    try {
      setEventsLoading(true);
      
      // Fetch local events
      const localEvents = await getEventsByUser(currentUser.uid);
      
      // Check Google Calendar connection and fetch Google events
      let googleEvents = [];
      let isConnected = false;
      
      try {
        const integrationsStatus = await getUserIntegrationsStatus(currentUser.uid);
        isConnected = integrationsStatus?.googleCalendar?.connected || false;
        setIsGoogleCalendarConnected(isConnected);
        
        if (isConnected) {
          // Window: last 60 days (for "Last Calls" history) through next 30 days (upcoming).
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - 60);
          const endDate = new Date();
          endDate.setDate(endDate.getDate() + 30);

          googleEvents = await fetchGoogleCalendarEvents(currentUser.uid, startDate, endDate);
          console.log('Google Calendar events fetched:', googleEvents);
        }
      } catch (googleError) {
        console.error('Error fetching Google Calendar events:', googleError);
        // Don't fail the entire function if Google Calendar fails
        googleEvents = [];
      }
      
      // Merge local and Google events
      const allEvents = [...localEvents, ...googleEvents];
      
      // Sort events by start time
      allEvents.sort((a, b) => {
        const aTime = a.startTime || a.start;
        const bTime = b.startTime || b.start;
        if (!aTime && !bTime) return 0;
        if (!aTime) return 1;
        if (!bTime) return -1;
        return new Date(aTime) - new Date(bTime);
      });
      
      setUserEvents(allEvents);
    } catch (err) {
      console.error('Error fetching user events:', err);
    } finally {
      setEventsLoading(false);
    }
  };

  // Helper functions for date calculations
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay(); // 0 = Sun, 1 = Mon
    const diff = day === 0 ? -6 : 1 - day; // shift to Monday
    d.setDate(d.getDate() + diff);
    return d;
  };

  const dateToISO = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatShortDate = (date) =>
    date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  const initialsFor = (presenter, fallback) => {
    if (!presenter) return fallback;
    return presenter
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const sessionFromDoc = (docSnap) => {
    const data = docSnap.data();
    const dateObj = data.date?.toDate ? data.date.toDate() : new Date(data.date);
    return {
      id: docSnap.id,
      date: dateObj,
      isoDate: dateToISO(dateObj),
      eventName: data.eventName || 'Breakthrough Session',
      presenter: data.presenter || '',
      presenterId: data.presenterId || '',
      presenterPhoto: data.presenterPhoto || '',
      presenterInitials: data.presenterInitials || '',
      status: data.status || 'active',
      department: data.department || 'General',
      description: data.description || '',
      startTime: data.startTime || '',
      endTime: data.endTime || '',
      needPresenter: data.needPresenter || false,
      archived: data.archived === true,
      label: dateObj.toLocaleDateString(undefined, { weekday: 'long' }),
    };
  };

  // A single breakthrough session card, using the same department-themed animated
  // gradient background as the client dashboard's Next Breakthrough Session hero.
  const renderBreakthroughCard = (session) => {
    const isNeedPresenter = session.needPresenter;
    const theme = getBreakthroughTheme(session.department);
    const initials =
      session.presenterInitials ||
      initialsFor(session.presenter, session.label?.slice(0, 2).toUpperCase() || 'BS');

    return (
      <div
        key={session.id}
        className="relative overflow-hidden rounded-lg text-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
        style={{ background: theme.gradient }}
        onClick={() => navigate('/crm/fulfillment/breakthroughsfufillment')}
      >
        <BreakthroughSessionBackground department={session.department} />
        <div className="relative z-10 p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            {session.presenterPhoto ? (
              <img
                src={session.presenterPhoto}
                alt={session.presenter || 'Presenter'}
                className="h-10 w-10 rounded-full object-cover ring-2 ring-white/40"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-white font-semibold text-xs">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white/70">{session.label}</div>
              <div className="text-xs text-white/85">{formatShortDate(session.date)}</div>
            </div>
            <span className="inline-flex shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
              {session.department || 'Unassigned'}
            </span>
          </div>

          <div className="flex flex-col">
            {isNeedPresenter ? (
              <>
                <div className="text-sm font-semibold text-white flex items-center gap-1">
                  <span>⚠️</span>
                  <span>Need Presenter</span>
                </div>
                <div className="text-xs text-white/85 mt-1">
                  {formatShortDate(session.date)} • {session.startTime || ''} {session.startTime && session.endTime ? '-' : ''} {session.endTime || ''}
                </div>
              </>
            ) : (
              <>
                <div className="text-sm font-semibold text-white">
                  {session.eventName}
                </div>
                <div className="text-xs text-white/85 mt-1">
                  {session.presenter ? `Presenter: ${session.presenter}` : 'No presenter yet'}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Fetch breakthrough sessions for this week and next week
  const fetchBreakthroughSessions = async () => {
    try {
      setSessionsLoading(true);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const thisWeekStart = getStartOfWeek(today);
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekEnd.getDate() + 6);
      thisWeekEnd.setHours(23, 59, 59, 999);
      
      const nextWeekStart = new Date(thisWeekEnd);
      nextWeekStart.setDate(nextWeekStart.getDate() + 1);
      nextWeekStart.setHours(0, 0, 0, 0);
      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekEnd.getDate() + 6);
      nextWeekEnd.setHours(23, 59, 59, 999);

      const upcomingStart = new Date(nextWeekEnd);
      upcomingStart.setDate(upcomingStart.getDate() + 1);
      upcomingStart.setHours(0, 0, 0, 0);
      const upcomingEnd = new Date(upcomingStart);
      upcomingEnd.setDate(upcomingEnd.getDate() + 42); // ~6 weeks out for upcoming

      const startBoundary = Timestamp.fromDate(thisWeekStart);
      const endBoundary = Timestamp.fromDate(upcomingEnd);

      const q = query(
        collection(db, 'breakthroughSessions'),
        where('date', '>=', startBoundary),
        where('date', '<=', endBoundary),
        orderBy('date', 'asc')
      );

      const snap = await getDocs(q);
      const thisWeek = [];
      const nextWeek = [];
      const upcoming = [];
      const seenDates = new Set(); // Track dates to prevent duplicates

      snap.forEach((docSnap) => {
        const session = sessionFromDoc(docSnap);
        if (session.archived) return;
        const sessionDate = new Date(session.date);
        sessionDate.setHours(0, 0, 0, 0);
        
        // Use isoDate as unique identifier to prevent duplicates
        const dateKey = session.isoDate;
        if (seenDates.has(dateKey)) {
          console.warn(`Duplicate session found for date ${dateKey}, skipping`);
          return;
        }
        seenDates.add(dateKey);
        
        if (sessionDate >= thisWeekStart && sessionDate <= thisWeekEnd) {
          thisWeek.push(session);
        } else if (sessionDate >= nextWeekStart && sessionDate <= nextWeekEnd) {
          nextWeek.push(session);
        } else if (sessionDate >= upcomingStart && sessionDate <= upcomingEnd) {
          upcoming.push(session);
        }
      });

      // Sort by date
      thisWeek.sort((a, b) => a.date - b.date);
      nextWeek.sort((a, b) => a.date - b.date);
      upcoming.sort((a, b) => a.date - b.date);
      const upcomingTwo = upcoming.slice(0, 2);

      // Backfill presenter photos from internal_users when missing
      const sessionsNeedingPhotos = [...thisWeek, ...nextWeek, ...upcomingTwo].filter(
        (s) => s.presenterId && !s.presenterPhoto
      );
      if (sessionsNeedingPhotos.length > 0) {
        const uniqueIds = Array.from(new Set(sessionsNeedingPhotos.map((s) => s.presenterId)));
        const photoEntries = await Promise.all(
          uniqueIds.map(async (id) => {
            try {
              const snap = await getDoc(doc(db, 'internal_users', id));
              if (!snap.exists()) return [id, ''];
              return [id, pickProfilePicUrl(snap.data())];
            } catch {
              return [id, ''];
            }
          })
        );
        const photoById = Object.fromEntries(photoEntries);
        const applyPhotos = (arr) =>
          arr.map((s) => ({
            ...s,
            presenterPhoto: s.presenterPhoto || photoById[s.presenterId] || '',
          }));
        setThisWeekSessions(applyPhotos(thisWeek));
        setNextWeekSessions(applyPhotos(nextWeek));
        setUpcomingSessions(applyPhotos(upcomingTwo));
        return;
      }

      setThisWeekSessions(thisWeek);
      setNextWeekSessions(nextWeek);
      setUpcomingSessions(upcomingTwo);
    } catch (err) {
      console.error('Error fetching breakthrough sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Fetch announcements
  const fetchAnnouncements = async () => {
    try {
      console.log('Fetching announcements...');
      setAnnouncementsLoading(true);
      const [announcementsWithStatus, unseenCount] = await Promise.all([
        getAnnouncementsWithSeenStatus(20), // Get 20 most recent announcements
        getUnseenAnnouncementsCount()
      ]);
      console.log('Announcements fetched:', announcementsWithStatus);
      console.log('Unseen count:', unseenCount);
      setAnnouncements(announcementsWithStatus);
      setUnseenCount(unseenCount);
    } catch (err) {
      console.error('Error fetching announcements:', err);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  // Fetch user's own announcements
  const fetchMyAnnouncements = async () => {
    try {
      console.log('Fetching my announcements for user:', currentUser?.uid);
      setAnnouncementsLoading(true);
      
      // Import Firebase functions directly to bypass target audience filtering
      const { collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      // Get ALL announcements without target audience filtering
      const announcementsRef = collection(db, 'announcements');
      const q = query(
        announcementsRef, 
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      
      const querySnapshot = await getDocs(q);
      const allAnnouncements = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      }));
      
      console.log('All announcements fetched (no filtering):', allAnnouncements);
      
      // Filter to show only announcements created by the current user
      const myAnnouncements = allAnnouncements.filter(announcement => {
        console.log('Checking announcement:', announcement.id, 'createdByUid:', announcement.createdByUid, 'currentUser:', currentUser?.uid);
        return announcement.createdByUid === currentUser?.uid;
      });
      
      console.log('My announcements filtered:', myAnnouncements);
      setAnnouncements(myAnnouncements);
    } catch (err) {
      console.error('Error fetching my announcements:', err);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  // Helper function to safely convert Firestore Timestamp to Date
  const convertToDate = convertToDateUtil;

  const filterTasksByTimeframe = filterTasksByTimeframeUtil;

  const filterEventsByTimeframe = filterEventsByTimeframeUtil;

  // Fetch client accounts once for Client Calls matching
  useEffect(() => {
    if (!currentUser) return;
    getAccountsByType('Clients')
      .then((accounts) =>
        setClientAccounts(
          accounts.filter((acc) => {
            const isArchived = acc.stage === 'Archived';
            const isDeleted =
              acc.isDeleted === true || acc.deleted === true || acc.status === 'Deleted' || acc.status === 'deleted';
            return !isArchived && !isDeleted;
          })
        )
      )
      .catch((err) => console.error('Error fetching client accounts:', err));
  }, [currentUser]);

  // Upcoming events that match a client account, soonest first
  const clientCalls = useMemo(() => {
    if (!clientAccounts.length) return [];
    const now = new Date();
    return userEvents
      .map((event) => {
        const start = convertToDateUtil(event.startTime || event.start);
        if (!start || start < now) return null;
        const account = matchEventToClientAccount(event, clientAccounts);
        if (!account) return null;
        return { ...event, callStart: start, account };
      })
      .filter(Boolean)
      .sort((a, b) => a.callStart - b.callStart);
  }, [userEvents, clientAccounts]);

  const selectedCall = clientCalls.find((c) => c.id === selectedCallId) || clientCalls[0] || null;
  const selectedCallAccountId = selectedCall?.account?.id || null;

  // Load the latest check-in + recorded call summaries for the selected call's company (debrief panel)
  useEffect(() => {
    if (!selectedCallAccountId) {
      setDebriefCheckIn(null);
      setAccountSummaries([]);
      return;
    }
    const account = clientAccounts.find((a) => a.id === selectedCallAccountId);
    let cancelled = false;

    const fetchLatestCheckIn = async () => {
      const checkInsRef = collection(db, 'check_ins');
      const snapshots = [];
      const byAccount = await getDocs(query(checkInsRef, where('accountId', '==', selectedCallAccountId)));
      snapshots.push(...byAccount.docs);
      const email = String(account?.contact?.email || '').trim();
      if (email) {
        const byEmail = await getDocs(query(checkInsRef, where('userEmail', '==', email)));
        snapshots.push(...byEmail.docs);
      }
      const merged = new Map();
      snapshots.forEach((d) => merged.set(d.id, d.data()));
      const rows = [...merged.values()]
        .map((d) => {
          const num = Number(d.overallSatisfaction ?? d.rating);
          return {
            rating: Number.isFinite(num) && num >= 1 && num <= 5 ? num : null,
            status: typeof d.overallStatus === 'string' ? d.overallStatus : null,
            comment: String(d.comments || d.additionalFeedback || '').trim(),
            date: checkInDateToJs(d.submittedAt || d.completedAt || d.createdAt),
          };
        })
        .filter((r) => r.rating != null || r.status)
        .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
      return rows[0] || null;
    };

    (async () => {
      try {
        setDebriefLoading(true);
        const [checkIn, summaries] = await Promise.all([
          fetchLatestCheckIn().catch((err) => {
            console.error('Error fetching debrief check-in:', err);
            return null;
          }),
          loadAccountMeetingSummaries(db, selectedCallAccountId).catch((err) => {
            console.error('Error fetching account meeting summaries:', err);
            return [];
          }),
        ]);
        if (!cancelled) {
          setDebriefCheckIn(checkIn);
          setAccountSummaries(summaries);
        }
      } finally {
        if (!cancelled) setDebriefLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCallAccountId, clientAccounts]);

  // The 3 most recent calls with the selected client, drawn from EVERY internal user's
  // calendar (recorded or not) and enriched with the matching Fathom summary when one exists.
  const debriefMeetings = useMemo(() => {
    if (!selectedCallAccountId) return [];
    const now = new Date();

    // Past calls with this client across all internal users, deduped by title+time.
    // When the same meeting sits on several calendars, keep the organizer's copy so the
    // "owner" is the team member who actually ran the call.
    const byKey = new Map();
    for (const event of allInternalEvents) {
      const start = convertToDateUtil(event.startTime || event.start);
      if (!start || start >= now) continue;
      const matched = matchEventToClientAccount(event, clientAccounts);
      if (!matched || matched.id !== selectedCallAccountId) continue;
      const key = `${normalizeMeetingTitle(event.title)}|${start.getTime()}`;
      const isOrganizer = event.organizer?.self === true;
      const existing = byKey.get(key);
      if (!existing || (isOrganizer && !existing.__isOrganizer)) {
        byKey.set(key, { event, start, __isOrganizer: isOrganizer });
      }
    }

    const usedSummaryIds = new Set();
    const eventItems = [...byKey.values()].map(({ event, start }) => {
      const summary = matchEventToMeetingSummary(event, accountSummaries, {
        accountId: selectedCallAccountId,
        internalEmails: internalUsers.map((u) => u.email),
      });
      if (summary) usedSummaryIds.add(summary.id);
      return {
        key: `evt_${event.ownerUid || 'x'}_${event.id}`,
        // Prefer the calendar owner (works even for un-recorded calls); fall back to the recorder.
        recorderUid: event.ownerUid || summary?.recorderUid || null,
        title: cleanEventTitle(event.title),
        brief: summary ? briefFathomSummary(summary) : '',
        callDate: start,
      };
    });

    // Recorded calls whose calendar event fell outside the fetched window.
    const orphanSummaryItems = accountSummaries
      .filter((s) => !usedSummaryIds.has(s.id))
      .map((s) => ({
        key: `sum_${s.id}`,
        recorderUid: s.recorderUid || null,
        title: cleanEventTitle(s.meetingTitle || ''),
        brief: briefFathomSummary(s),
        callDate: checkInDateToJs(s.scheduledStartTime || s.createdAt),
      }));

    return [...eventItems, ...orphanSummaryItems]
      .filter((m) => m.callDate)
      .sort((a, b) => b.callDate - a.callDate)
      .slice(0, 3);
  }, [selectedCallAccountId, allInternalEvents, clientAccounts, accountSummaries, internalUsers]);

  // Fetch tasks when component mounts or user changes
  useEffect(() => {
    fetchUserTasks();
  }, [currentUser]);

  // Fetch events when component mounts or user changes
  useEffect(() => {
    fetchUserEvents();
  }, [currentUser]);

  // Fetch breakthrough sessions when component mounts
  useEffect(() => {
    fetchBreakthroughSessions();
  }, []);

  // Auto-advance the merged gallery every few seconds; pause on hover and
  // reset the timer whenever the active panel changes (e.g. a manual dot click).
  useEffect(() => {
    if (galleryPaused) return undefined;
    const timer = setInterval(() => setActivePanel((p) => (p + 1) % 2), 6000);
    return () => clearInterval(timer);
  }, [galleryPaused, activePanel]);

  // Fetch announcements when component mounts
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Keep the unread badge live so it clears as soon as the user reads a thread,
  // instead of staying stale until the dashboard is reloaded.
  useEffect(() => {
    if (!currentUser?.uid) {
      setUnreadMessages(0);
      return;
    }
    const unsubscribe = subscribeToTotalUnreadCount(currentUser.uid, setUnreadMessages);
    return unsubscribe;
  }, [currentUser]);

  // Load user preferences when component mounts or user changes
  useEffect(() => {
    loadUserPreferences();
  }, [currentUser]);

  // Determine user type
  useEffect(() => {
    const determineUserType = async () => {
      if (!currentUser) return;
      
      try {
        // Check if user exists in internal_users collection
        const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (internalUserDoc.exists()) {
          setUserType('internal');
          setInternalCrmRole(internalUserDoc.data()?.role ?? null);
          setUserTeam(internalUserDoc.data()?.team ?? null);
          setAnnouncementType('company'); // Default to company for internal users
        } else {
          setUserType('portal');
          setInternalCrmRole(null);
          setUserTeam(null);
          setAnnouncementType('client'); // Default to client for portal users
        }
      } catch (err) {
        console.error('Error determining user type:', err);
        setUserType('portal'); // Default to portal
        setInternalCrmRole(null);
        setUserTeam(null);
        setAnnouncementType('client'); // Default to client for portal users
      }
    };
    
    determineUserType();
  }, [currentUser]);


  // Filter tasks when timeframe changes
  useEffect(() => {
    console.log('Filtering tasks - userTasks:', userTasks.length, 'selectedTaskPeriod:', selectedTaskPeriod);
    // Only show not done tasks
    const filtered = filterTasksByTimeframe(userTasks, selectedTaskPeriod).filter(task => {
      const isNotCompleted = !task.completed;
      if (!isNotCompleted) {
        console.log('Filtering out completed task:', task.title);
      }
      return isNotCompleted;
    });
    console.log('Final filtered tasks count:', filtered.length);
    setFilteredTasks(filtered);
  }, [userTasks, selectedTaskPeriod]);

  // Filter events when timeframe changes
  useEffect(() => {
    const filtered = filterEventsByTimeframe(userEvents, selectedEventPeriod);
    setFilteredEvents(filtered);
  }, [userEvents, selectedEventPeriod]);

  // Debug logging for unread messages
  console.log('Dashboard unread messages state:', {
    unreadMessages,
    loading,
    currentUser: currentUser?.uid
  });

  const canAccessOwnerExecutiveCrm =
    internalCrmRole != null && CRM_OWNER_EXECUTIVE_ROLES.includes(internalCrmRole);

  // HR team members get the same stats / My Tasks / Client Calls cards as
  // everyone else, with the HR fulfillment widgets in place of the gallery.
  const isHrTeam = userType === 'internal' && userTeam === 'HR';
  const isMarketingTeam = userType === 'internal' && userTeam === 'Marketing';

  // Leads & Clients chart — load every lead + client account once for
  // Owner / Executive users. Converted leads change type to 'Clients', so both
  // types are needed to count leads received per month.
  useEffect(() => {
    if (!canAccessOwnerExecutiveCrm) return undefined;
    let cancelled = false;
    (async () => {
      setLeadsClientsLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, 'accounts'), where('type', 'in', ['Leads', 'Clients']))
        );
        if (!cancelled) {
          setLeadsClientsAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error('Error loading accounts for leads & clients chart:', err);
        if (!cancelled) setLeadsClientsAccounts([]);
      } finally {
        if (!cancelled) setLeadsClientsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccessOwnerExecutiveCrm]);

  // Chart data for the selected timeframe. Buckets follow the range: days for
  // the current month, weeks for the last 3 months, months for 6 months / YTD.
  // New leads = leads received in the bucket; total clients = running count of
  // active clients at the bucket's end. Future buckets are null so lines stop
  // at today. Also returns headline totals for the summary strip.
  const leadsClientsChart = useMemo(() => {
    const empty = { points: [], newLeadsTotal: 0, prevLeadsTotal: 0, clientsNow: 0, clientsAtStart: 0 };
    if (!leadsClientsAccounts) return empty;
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    const leads = leadsClientsAccounts
      .filter((a) => a.showOnKanban !== false)
      .map((a) => getLeadReceivedDate(a))
      .filter(Boolean);
    const clients = leadsClientsAccounts
      .filter((a) => a.type === 'Clients' && isActiveClientAccount(a))
      .map((a) => getClientSignedDate(a))
      .filter(Boolean);

    const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    const countLeads = (s, e) => leads.filter((d) => d >= s && d <= e).length;
    const countClients = (e) => clients.filter((d) => d <= e).length;
    const shortDate = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

    const points = [];
    let rangeStart;

    if (leadsClientsRange === 'month') {
      rangeStart = new Date(y, m, 1);
      const daysInMonth = new Date(y, m + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const s = new Date(y, m, day);
        const e = endOfDay(s);
        const future = s > now;
        points.push({
          label: String(day),
          fullLabel: shortDate(s),
          newLeads: future ? null : countLeads(s, e),
          totalClients: future ? null : countClients(e),
        });
      }
    } else if (leadsClientsRange === '3m') {
      // Weekly buckets from the 1st of the month two months back through today.
      rangeStart = new Date(y, m - 2, 1);
      let s = new Date(rangeStart);
      while (s <= now) {
        const weekEnd = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
        const e = endOfDay(weekEnd > now ? now : weekEnd);
        points.push({
          label: shortDate(s),
          fullLabel: `Week of ${shortDate(s)}`,
          newLeads: countLeads(s, e),
          totalClients: countClients(e),
        });
        s = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 7);
      }
    } else {
      const monthsBack = leadsClientsRange === '6m' ? 6 : m + 1;
      rangeStart = new Date(y, m - (monthsBack - 1), 1);
      for (let i = monthsBack - 1; i >= 0; i -= 1) {
        const s = new Date(y, m - i, 1);
        const e = endOfDay(new Date(s.getFullYear(), s.getMonth() + 1, 0));
        points.push({
          label: `${MONTH_SHORT[s.getMonth()]}${s.getFullYear() !== y ? ` '${String(s.getFullYear()).slice(-2)}` : ''}`,
          fullLabel: `${MONTH_SHORT[s.getMonth()]} ${s.getFullYear()}`,
          newLeads: countLeads(s, e),
          totalClients: countClients(e),
        });
      }
    }

    const rangeEnd = endOfDay(now);
    const spanMs = rangeEnd.getTime() - rangeStart.getTime();
    const prevEnd = new Date(rangeStart.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - spanMs);
    return {
      points,
      newLeadsTotal: countLeads(rangeStart, rangeEnd),
      prevLeadsTotal: countLeads(prevStart, prevEnd),
      clientsNow: countClients(rangeEnd),
      clientsAtStart: countClients(prevEnd),
    };
  }, [leadsClientsAccounts, leadsClientsRange]);

  const stats = [
    {
      id: 1,
      title: 'New Leads',
      hint: 'Last 14 days',
      value: loading ? '...' : dashboardMetrics.newLeads.toString(),
      emoji: '🌱',
      accent: 'orange',
      onClick: () => navigate(canAccessOwnerExecutiveCrm ? '/crm/leads' : '/crm/clients')
    },
    {
      id: 2,
      title: 'New Clients',
      hint: 'Last 14 days',
      value: loading ? '...' : dashboardMetrics.newClients.toString(),
      emoji: '🤝',
      accent: 'blue',
      onClick: () => navigate('/crm/clients')
    },
    {
      id: 3,
      title: 'Messages',
      hint: 'Unread',
      value: loading ? '...' : unreadMessages.toString(),
      emoji: '✉️',
      accent: 'emerald',
      onClick: () => navigate('/crm/messages')
    },
    {
      id: 4,
      title: 'Questions',
      hint: 'Open inquiries',
      value: loading ? '...' : dashboardMetrics.questions.toString(),
      emoji: '❓',
      accent: 'purple',
      onClick: () => navigate('/crm/questions')
    },
    {
      id: 5,
      title: 'Fires',
      hint: 'Urgent matters',
      value: loading ? '...' : dashboardMetrics.fires.toString(),
      emoji: '🔥',
      accent: 'red',
      onClick: () => navigate('/crm/fires')
    },
    {
      id: 6,
      title: 'Announcements',
      hint: 'Unseen updates',
      value: announcementsLoading ? '...' : unseenCount.toString(),
      emoji: '📣',
      accent: 'indigo',
      onClick: () => setShowAnnouncementsModal(true)
    }
  ].filter((s) => (internalCrmRole !== 'Member' && !isHrTeam) || s.title !== 'New Leads');

  const toggleTasksDropdown = () => {
    setTasksDropdownOpen(!tasksDropdownOpen);
    if (eventsDropdownOpen) setEventsDropdownOpen(false);
  };

  const toggleEventsDropdown = () => {
    setEventsDropdownOpen(!eventsDropdownOpen);
    if (tasksDropdownOpen) setTasksDropdownOpen(false);
  };

  const handleTaskPeriodSelect = (period) => {
    setSelectedTaskPeriod(period);
    saveTaskPeriodPreference(period);
    setTasksDropdownOpen(false);
  };

  const handleEventPeriodSelect = (period) => {
    setSelectedEventPeriod(period);
    saveEventPeriodPreference(period);
    setEventsDropdownOpen(false);
  };

  const handleTaskCompletion = async (taskId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const task = userTasks.find(t => t.id === taskId);
      if (!task) return;

      // Update the task completion status
      const updatedTask = { ...task, completed: !task.completed };
      setUserTasks(prevTasks => 
        prevTasks.map(t => t.id === taskId ? updatedTask : t)
      );
      
      // TODO: Call the actual update function from taskService
      await toggleTaskCompletion(taskId, !task.completed);
    } catch (err) {
      console.error('Error toggling task completion:', err);
    }
  };

  const handleEventClick = (event) => {
    // Navigate to calendar page - the calendar will handle showing event details
    navigate('/calendar');
  };


  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.trim()) return;
    
    try {
      setIsUploading(true);
      setFileUploadError('');
      
      let fileData = {};
      
      // Upload file if selected
      if (selectedFile) {
        try {
          fileData = await uploadAnnouncementFile(selectedFile);
        } catch (err) {
          setFileUploadError(err.message);
          setIsUploading(false);
          return;
        }
      }
      
      // Create announcement with file data and type
      await createAnnouncement({ 
        message: newAnnouncement.trim(),
        type: announcementType, // Pass the announcement type
        ...fileData
      });
      
      // Store the current form state before resetting
      const wasInMyAnnouncementsMode = showNewAnnouncementForm;
      console.log('Was in my announcements mode:', wasInMyAnnouncementsMode);
      
      // Reset form
      setNewAnnouncement('');
      setSelectedFile(null);
      setShowNewAnnouncementForm(false);
      setFileUploadError('');
      setAnnouncementType(userType === 'internal' ? 'company' : 'client'); // Reset to appropriate default
      
      // Refresh announcements based on the previous state
      if (wasInMyAnnouncementsMode) {
        console.log('Refreshing my announcements after creation...');
        // Add a small delay to ensure the database has been updated
        setTimeout(() => {
          fetchMyAnnouncements(); // Refresh my announcements
        }, 500);
      } else {
        console.log('Refreshing regular announcements after creation...');
        fetchAnnouncements(); // Refresh regular announcements
      }
    } catch (err) {
      console.error('Error creating announcement:', err);
      setFileUploadError('Failed to create announcement. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleMarkAsSeen = async (announcementId) => {
    try {
      await markAnnouncementAsSeen(announcementId);
      // Update local state
      setAnnouncements(prev => 
        prev.map(announcement => 
          announcement.id === announcementId 
            ? { ...announcement, seen: true }
            : announcement
        )
      );
      // Refresh unseen count
      const newUnseenCount = await getUnseenAnnouncementsCount();
      setUnseenCount(newUnseenCount);
    } catch (err) {
      console.error('Error marking announcement as seen:', err);
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    try {
      await deleteAnnouncement(announcementId);
      // Refresh the appropriate announcements list
      if (showNewAnnouncementForm) {
        fetchMyAnnouncements(); // Refresh my announcements
      } else {
        // Remove from local state for regular announcements
        setAnnouncements(prev => prev.filter(announcement => announcement.id !== announcementId));
        // Refresh unseen count
        const newUnseenCount = await getUnseenAnnouncementsCount();
        setUnseenCount(newUnseenCount);
      }
    } catch (err) {
      console.error('Error deleting announcement:', err);
      alert('Only the creator can delete this announcement');
    }
  };

  // File handling functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileUploadError('');
    }
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setFileUploadError('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      case 'txt':
        return '📄';
      default:
        return '📎';
    }
  };

  const formatValue = (value, type) => {
    return value;
  };

  const getYAxisLabel = () => {
    switch(selectedGoal) {
      case 'leads':
        return 'Number of Leads';
      case 'clients':
        return 'Number of Clients';
      default:
        return '';
    }
  };

  // Fetch internal users and accounts on mount
  useEffect(() => {
    const fetchUsersAndAccounts = async () => {
      try {
        const [users, accs] = await Promise.all([
          getInternalUsers(),
          getAccounts()
        ]);
        setInternalUsers(users);
        setAccounts(accs);
      } catch (err) {
        // Optionally handle error
      }
    };
    fetchUsersAndAccounts();
  }, []);

  // Fetch recent past calendar events for EVERY internal user so "Last Calls" reflects
  // the whole team's history with a client, not just the current user's calendar.
  useEffect(() => {
    if (!internalUsers.length) return;
    let cancelled = false;
    (async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90);
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 1);
      const perUser = await Promise.all(
        internalUsers.map(async (u) => {
          try {
            const evts = await fetchGoogleCalendarEvents(u.id, startDate, endDate);
            return (evts || []).map((e) => ({ ...e, ownerUid: u.id }));
          } catch {
            return [];
          }
        })
      );
      if (!cancelled) setAllInternalEvents(perUser.flat());
    })();
    return () => {
      cancelled = true;
    };
  }, [internalUsers]);

  // Handle click for task or event
  const handleTaskOrEventClick = async (item, type = 'task') => {
    setTaskDetailsLoading(true);
    setTaskDetailsError(null);
    setSelectedTaskOrEvent(null);
    setTaskDetailsComments([]);
    try {
      let details = { ...item };
      let comments = [];
      if (type === 'task') {
        // Get comments for the task
        comments = await getTaskComments(item.id);
        // Get full assignee details for the task
        const assigneesWithDetails = (item.assignees || []).map(assigneeId => {
          const internalUser = internalUsers.find(u => u.id === assigneeId);
          if (internalUser) return internalUser;
          const account = accounts.find(a => a.id === assigneeId);
          if (account) {
            return {
              id: account.id,
              name: account.company,
              type: 'client',
              initials: account.company?.[0] || 'C'
            };
          }
          return null;
        }).filter(Boolean);
        details.assignees = Array.isArray(item.assignees) && item.assignees[0]?.name 
          ? item.assignees 
          : assigneesWithDetails;
      } else if (type === 'event') {
        // For events, you can add more detail fetching if needed
        details.assignees = [];
      }
      setSelectedTaskOrEvent({ ...details, type });
      setTaskDetailsComments(comments);
      setShowTaskDetailsModal(true);
    } catch (err) {
      setTaskDetailsError('Failed to load details. Please try again.');
    } finally {
      setTaskDetailsLoading(false);
    }
  };

  const handleAddTaskComment = async () => {
    if (!newTaskComment.trim() || !selectedTaskOrEvent) return;
    setIsAddingComment(true);
    try {
      const currentUserObj = internalUsers.find(u => u.id === currentUser?.uid);
      let fileData = {};
      if (commentFile) {
        // If you have a file upload function, use it here. Otherwise, just attach file name.
        // For now, just attach file name and URL as placeholder
        fileData = {
          fileName: commentFile.name,
          fileType: commentFile.type,
          fileUrl: URL.createObjectURL(commentFile) // Replace with real upload logic if needed
        };
      }
      const commentData = {
        message: newTaskComment.trim(),
        userName: currentUserObj?.name || 'Unknown User',
        userInitials: currentUserObj?.initials || 'UU',
        ...fileData
      };
      const newCommentDoc = await addTaskComment(selectedTaskOrEvent.id, commentData);
      setTaskDetailsComments(prev => [...prev, newCommentDoc]);
      setNewTaskComment('');
      setCommentFile(null);
      setCommentFileError('');
    } catch (err) {
      setCommentFileError('Failed to add comment.');
    } finally {
      setIsAddingComment(false);
    }
  };

  const { isMobile, isTablet } = useResponsive();

  return (
    <div className={`${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-6'} min-h-screen`}>
      <style>{shineStyles}</style>
      {/* Same ambient BBA background the /start and portal dashboards use */}
      <PortalBackground />
      <div className="relative">
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className={`grid gap-3 ${isMobile ? 'grid-cols-2' : stats.length === 5 ? 'grid-cols-5' : 'grid-cols-6'} w-full`}>
          {(() => {
            const accentStyles = {
              orange: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
              blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
              emerald: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
              purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
              red: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
              indigo: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
            };
            const accentText = {
              orange: 'text-orange-600 dark:text-orange-400',
              blue: 'text-blue-600 dark:text-blue-400',
              emerald: 'text-emerald-600 dark:text-emerald-400',
              purple: 'text-purple-600 dark:text-purple-400',
              red: 'text-red-600 dark:text-red-400',
              indigo: 'text-indigo-600 dark:text-indigo-400',
            };
            return stats.map((stat) => {
              const isZero = stat.value === '0' || stat.value === '...';
              return (
                <button
                  key={stat.id}
                  type="button"
                  onClick={stat.onClick}
                  className={`group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${isMobile ? 'p-3' : 'p-4'}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`${isMobile ? 'w-9 h-9' : 'w-11 h-11'} rounded-xl flex items-center justify-center shrink-0 ${accentStyles[stat.accent]}`}
                    >
                      <span className={isMobile ? 'text-xl' : 'text-2xl'} aria-hidden="true">{stat.emoji}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold leading-none ${
                          isZero ? 'text-gray-300 dark:text-gray-600' : accentText[stat.accent]
                        }`}
                      >
                        {stat.value === '...' ? '…' : stat.value}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{stat.title}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors shrink-0" />
                  </div>
                </button>
              );
            });
          })()}
        </div>

        {/* Error Display */}
        {error && (
          <div className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg ${isMobile ? 'p-3' : 'p-4'}`}>
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error loading dashboard data
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* My Tasks & Client Calls Section */}
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 lg:grid-cols-12 gap-6'}`}>
          {/* My Tasks */}
          <div className={`lg:col-span-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-5'} flex flex-col`}>
            <div className="flex items-center justify-between gap-2 mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-base leading-none" aria-hidden="true">✅</span>
                My Tasks
              </h2>
            </div>
            {tasksLoading ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <BBALoader size={32} />
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <img src="/caught-up.svg" alt="All caught up" className="w-12 h-12 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">You're all caught up!</p>
              </div>
            ) : (
              <div className="flex-1 space-y-1.5 max-h-80 overflow-y-auto">
                {filteredTasks.map((task) => {
                  const dueDate = convertToDate(task.dueDate);
                  const isOverdue = dueDate && dueDate < new Date() && !task.completed;
                  return (
                    <div
                      key={task.id}
                      className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                      onClick={() => handleTaskOrEventClick(task, 'task')}
                    >
                      <button
                        onClick={(e) => handleTaskCompletion(task.id, e)}
                        className="mt-0.5 w-5 h-5 rounded-full border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600 flex items-center justify-center shrink-0 hover:border-red-500 transition-colors"
                      >
                        {task.completed && <Check size={14} className="text-white" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>
                          {task.title}
                        </p>
                        {dueDate && (
                          <p className={`text-xs ${isOverdue ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                            Due {dueDate.toLocaleDateString()}
                            {isOverdue && ' · Overdue'}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              onClick={() => navigate('/crm/tasks')}
              className="mt-4 w-full py-2 px-4 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              View All Tasks
            </button>
          </div>
          {/* Client Calls */}
          <div className={`lg:col-span-8 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-5'} flex flex-col`}>
            <div className="flex items-center justify-between gap-2 mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-base leading-none" aria-hidden="true">🎥</span>
                Client Calls
              </h2>
            </div>
            {eventsLoading ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <BBALoader size={32} />
              </div>
            ) : clientCalls.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <Video className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No upcoming client calls</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Calls with client attendees will show up here automatically.</p>
              </div>
            ) : (
              <div className={`flex-1 grid ${isMobile ? 'grid-cols-1' : 'grid-cols-5'} gap-4 min-h-0`}>
                {/* Upcoming calls list */}
                <div className={`${isMobile ? '' : 'col-span-2'} space-y-1.5 max-h-96 overflow-y-auto pr-1`}>
                  {clientCalls.slice(0, 8).map((call) => {
                    const isSelected = call.id === selectedCall?.id;
                    const listPhoto = getCrmContactDisplayPhotoUrl(call.account);
                    const listContact = call.account.contact || {};
                    const listName =
                      [listContact.firstName, listContact.lastName].filter(Boolean).join(' ') ||
                      call.account.company;
                    const listInitials =
                      `${listContact.firstName?.[0] || ''}${listContact.lastName?.[0] || ''}`.toUpperCase() ||
                      String(call.account.company || '?').slice(0, 2).toUpperCase();
                    // Show the company under the name only when the name isn't already the company.
                    const listCompany =
                      call.account.company && call.account.company !== listName ? call.account.company : '';
                    return (
                      <div
                        key={call.id}
                        className={`w-full flex items-stretch rounded-lg transition-colors border ${
                          isSelected
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setSelectedCallId(call.id)}
                          className="flex-1 min-w-0 text-left flex items-start gap-2.5 px-2.5 py-2"
                        >
                          {listPhoto ? (
                            <img
                              src={listPhoto}
                              alt={call.account.company}
                              className="mt-0.5 w-8 h-8 rounded-full object-cover shrink-0"
                            />
                          ) : (
                            <div className="mt-0.5 w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 flex items-center justify-center shrink-0 text-[10px] font-semibold">
                              {listInitials}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{listName}</p>
                            {listCompany && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{listCompany}</p>
                            )}
                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                              <CalendarClock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <span className="truncate">
                                {formatCallDayLabel(call.callStart)} at{' '}
                                {call.callStart.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                              </span>
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`/crm/client/${call.account.id}`)}
                          title="Open client overview"
                          aria-label={`Open client overview for ${call.account.company || listName}`}
                          className="shrink-0 flex items-center justify-center px-2.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                {/* Company debrief */}
                <div className={`${isMobile ? '' : 'col-span-3'} rounded-lg bg-gray-50 dark:bg-gray-800/60 ${isMobile ? 'p-3' : 'p-4'} flex flex-col`}>
                  {selectedCall && (() => {
                    const account = selectedCall.account;
                    // Agenda the client submitted when booking (lives in the event description).
                    const agendaRows = parseAgendaFromDescription(selectedCall.description);
                    // Which department page this call was booked from, for the summary rundown.
                    const callDept = DEBRIEF_DEPARTMENTS.find((d) => matchesDepartmentTitle(selectedCall.title, d));
                    const deptInfo = callDept ? account.departments?.[callDept] : null;
                    // Roadmap "next up": current in-progress item first, mirroring Client Overview's summary tab.
                    const roadmapItems = (() => {
                      const visible = (account.roadmapTemplates || []).filter((t) => (t.status || '') !== 'hidden');
                      const sorted = [...visible].sort(
                        (a, b) => (a.order || a.allOrder || 0) - (b.order || b.allOrder || 0)
                      );
                      const isInProgress = (t) =>
                        ['in-progress', 'in_progress', 'active'].includes((t.status || '').toLowerCase());
                      const isCompleted = (t) =>
                        ['completed', 'done'].includes((t.status || '').toLowerCase());
                      let startIdx = sorted.findIndex(isInProgress);
                      if (startIdx < 0) startIdx = sorted.findIndex((t) => !isCompleted(t));
                      if (startIdx < 0) return [];
                      return sorted.slice(startIdx, startIdx + 3);
                    })();
                    return (
                      <>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Latest Check-In</p>
                            {debriefLoading ? (
                              <span className="text-xs text-gray-400 dark:text-gray-500">Loading…</span>
                            ) : debriefCheckIn ? (
                              <>
                                <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 dark:text-white">
                                  <Star className="w-3.5 h-3.5 fill-current text-amber-500" />
                                  {debriefCheckIn.rating != null ? `${debriefCheckIn.rating}/5` : debriefCheckIn.status}
                                </span>
                                {debriefCheckIn.date && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {debriefCheckIn.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                                {debriefCheckIn.comment && (
                                  <span className="relative group inline-flex">
                                    <MessageSquare className="w-4 h-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer" />
                                    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-80 max-w-[80vw] rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-sm leading-relaxed px-4 py-3 shadow-xl z-20">
                                      “{debriefCheckIn.comment}”
                                    </span>
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-sm text-gray-500 dark:text-gray-400">No check-ins yet</span>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-3 flex-wrap">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Last Calls</p>
                            {debriefLoading ? (
                              <span className="text-xs text-gray-400 dark:text-gray-500">Loading…</span>
                            ) : debriefMeetings.length > 0 ? (
                              debriefMeetings.map((meeting) => {
                                const recorder = internalUsers.find((u) => u.id === meeting.recorderUid);
                                const recorderPhoto =
                                  recorder?.profileImage || recorder?.photoURL || recorder?.avatarUrl || '';
                                const recorderFirstName =
                                  recorder?.firstName || (recorder?.name || '').split(' ')[0] || '—';
                                return (
                                  <span
                                    key={meeting.key}
                                    className="inline-flex items-center gap-1.5"
                                    title={meeting.brief || meeting.title || ''}
                                  >
                                    {recorderPhoto ? (
                                      <img
                                        src={recorderPhoto}
                                        alt={recorder?.name || 'Team member'}
                                        className="w-5 h-5 rounded-full object-cover shrink-0"
                                      />
                                    ) : (
                                      <span className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 flex items-center justify-center text-[9px] font-semibold shrink-0">
                                        {recorder?.initials || '?'}
                                      </span>
                                    )}
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{recorderFirstName}</span>
                                    {meeting.callDate && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {meeting.callDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                      </span>
                                    )}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">No calls on file yet</span>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Deep Debrief</p>
                          <div className="space-y-2.5">
                            <div>
                              <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">Call Agenda</p>
                              {agendaRows.length > 0 ? (
                                agendaRows.map((row, i) => (
                                  <p key={i} className="text-xs text-gray-700 dark:text-gray-200">{row}</p>
                                ))
                              ) : (
                                <p className="text-xs text-gray-400 dark:text-gray-500 italic">No agenda submitted for this call.</p>
                              )}
                            </div>
                            {roadmapItems.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-900 dark:text-white mb-1">Roadmap — Next Up</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {roadmapItems.map((item, i) => {
                                    const s = (item.status || '').toLowerCase();
                                    const inProgress = ['in-progress', 'in_progress', 'active'].includes(s);
                                    return (
                                      <span
                                        key={String(item.clientRoadmapId ?? item.id ?? i)}
                                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                                          inProgress
                                            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                                            : 'bg-gray-200/70 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                        }`}
                                      >
                                        {item.title || 'Untitled'}
                                        {inProgress ? ' · In Progress' : ''}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {deptInfo?.summary && (
                              <div>
                                <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">
                                  {DEBRIEF_DEPARTMENT_LABELS[callDept]} Summary
                                </p>
                                <p className="text-xs text-gray-700 dark:text-gray-200 line-clamp-3" title={deptInfo.summary}>
                                  {deptInfo.summary}
                                </p>
                              </div>
                            )}
                            {(account.metrics?.currentRevenue || account.metrics?.revenueGoal) ? (
                              <p className="text-xs text-gray-700 dark:text-gray-200">
                                <span className="font-semibold text-gray-900 dark:text-white">Revenue:</span>{' '}
                                ${Number(account.metrics?.currentRevenue || 0).toLocaleString()} of $
                                {Number(account.metrics?.revenueGoal || 0).toLocaleString()} goal
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="mt-auto pt-3 flex items-center gap-2">
                          {selectedCall.hangoutLink && (
                            <a
                              href={selectedCall.hangoutLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold text-center bg-red-600 text-white hover:bg-red-700 transition-colors"
                            >
                              Join Call
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/crm/client/${account.id}`)}
                            className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold bg-gray-200/70 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors inline-flex items-center justify-center gap-1"
                          >
                            View Client
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Merged rotating gallery: Breakthrough Sessions · Client Check-Ins · Department Updates */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-stretch">
            {/* Right (1/4) — rotating gallery: Breakthrough Sessions · Client Check-Ins */}
            <div
              className={`${canAccessOwnerExecutiveCrm || isHrTeam || isMarketingTeam ? 'lg:col-span-1' : 'lg:col-span-4'} lg:order-2 relative bg-white dark:bg-gray-900 p-6 rounded-lg shadow-sm hover-lift flex flex-col`}
              onMouseEnter={() => setGalleryPaused(true)}
              onMouseLeave={() => setGalleryPaused(false)}
            >
              <div className="min-h-[440px] flex-1">
            {/* Panel 1 — Client Check-Ins */}
            {activePanel === 1 && (
            <div key="checkins" className="dashboard-gallery-fade">
              <div className="flex flex-col items-center mb-4">
                <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-semibold dark:text-white flex items-center gap-2`}>
                  <span className="text-2xl leading-none shrink-0" aria-hidden="true">⭐</span>
                  Client Check Ins
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {new Date(currentDate.year, currentDate.month - 1).toLocaleString('default', { month: 'long' })} {currentDate.year}
                </p>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center w-full min-h-[140px]">
                  <BBALoader size={40} className="mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Loading check-ins...</p>
                </div>
              ) : checkInStats.totalRows === 0 ? (
                <div className="flex flex-col items-center justify-center w-full min-h-[140px]">
                  <div className="text-center px-2">
                    <div className="mb-3 flex items-center justify-center">
                      <img src="/caught-up.svg" alt="" className="w-12 h-12 opacity-80" />
                    </div>
                    <div className="text-gray-500 dark:text-gray-300 font-medium mb-1">No check-ins this month</div>
                    <div className="text-gray-400 dark:text-gray-500 text-sm max-w-sm">
                      Scheduled or manual client check-ins for this month will show response counts and satisfaction ratings here.
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/80 p-3">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{checkInStats.responded}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Responses</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/80 p-3">
                      <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{checkInStats.pending}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Pending</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/80 p-3">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {checkInStats.avgRating != null ? checkInStats.avgRating.toFixed(1) : '—'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Avg / 5</div>
                    </div>
                  </div>

                  {(() => {
                    const pieData = [
                      { name: 'Pending', value: checkInStats.pending, color: '#f59e0b' },
                      { name: '1 Star', value: checkInStats.distribution[0], color: '#ef4444' },
                      { name: '2 Stars', value: checkInStats.distribution[1], color: '#f97316' },
                      { name: '3 Stars', value: checkInStats.distribution[2], color: '#eab308' },
                      { name: '4 Stars', value: checkInStats.distribution[3], color: '#84cc16' },
                      { name: '5 Stars', value: checkInStats.distribution[4], color: '#22c55e' },
                    ].filter((d) => d.value > 0);
                    if (pieData.length === 0) return null;
                    return (
                      <div className="mt-5">
                        <div className="h-40">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={38}
                                outerRadius={64}
                                paddingAngle={2}
                                stroke="none"
                              >
                                {pieData.map((entry) => (
                                  <Cell key={entry.name} fill={entry.color} />
                                ))}
                              </Pie>
                              <RechartsTooltip
                                formatter={(value, name) => [value, name]}
                                contentStyle={{
                                  borderRadius: '0.5rem',
                                  border: 'none',
                                  fontSize: '0.75rem',
                                  background: darkMode ? '#1f2937' : '#ffffff',
                                  color: darkMode ? '#f9fafb' : '#111827',
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1">
                          {pieData.map((entry) => (
                            <div key={entry.name} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: entry.color }} />
                              {entry.name} ({entry.value})
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}

              <button
                type="button"
                onClick={() => navigate('/crm/client-satisfaction')}
                className="mt-4 w-full py-2 px-3 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors inline-flex items-center justify-center gap-1"
              >
                View Full Report
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            )}

            {/* Panel 0 — Breakthrough Sessions */}
            {activePanel === 0 && (
            <div key="breakthrough" className="dashboard-gallery-fade">
              <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-semibold ${isMobile ? 'mb-3' : 'mb-4'} text-center dark:text-white`}>Breakthrough Sessions</h2>
              <div className="grid grid-cols-1 gap-4">
                {/* This Week */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">This Week</h3>
                  {sessionsLoading ? (
                    <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
                      Loading sessions...
                    </div>
                  ) : thisWeekSessions.length === 0 ? (
                    <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-2">
                      No sessions this week
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {thisWeekSessions.map(renderBreakthroughCard)}
                    </div>
                  )}
                </div>

                {/* Next Week */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Next Week</h3>
                  {sessionsLoading ? (
                    <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
                      Loading sessions...
                    </div>
                  ) : nextWeekSessions.length === 0 ? (
                    <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-2">
                      No sessions next week
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {nextWeekSessions.map(renderBreakthroughCard)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}
              </div>

              {/* Gallery indicators */}
              <div className="flex items-center justify-center gap-2 mt-6">
                {['Breakthrough Sessions', 'Client Check-Ins'].map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setActivePanel(i)}
                    aria-label={label}
                    title={label}
                    className={`h-2 rounded-full transition-all ${
                      activePanel === i
                        ? 'w-6 bg-red-500'
                        : 'w-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Left (3/4) — HR team: fulfillment position-request tracker */}
            {isHrTeam && (
              <HRFulfillmentWidgets className="lg:col-span-3 lg:order-1" isMobile={isMobile} />
            )}

            {/* Left (3/4) — Marketing team: fulfillment service-line tracker */}
            {isMarketingTeam && (
              <MarketingFulfillmentWidgets className="lg:col-span-3 lg:order-1" isMobile={isMobile} />
            )}

            {/* Left (3/4) — Leads & Clients chart, Owner / Executive only */}
            {canAccessOwnerExecutiveCrm && !isHrTeam && !isMarketingTeam && (() => {
              const { points, newLeadsTotal, prevLeadsTotal, clientsNow, clientsAtStart } = leadsClientsChart;
              const leadsDelta = newLeadsTotal - prevLeadsTotal;
              const clientsDelta = clientsNow - clientsAtStart;
              const ink = darkMode ? '#9CA3AF' : '#6B7280';
              const gridInk = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(17,24,39,0.08)';
              const surface = darkMode ? '#111827' : '#FFFFFF';
              const rangeLabel = LEADS_CLIENTS_RANGES.find((r) => r.key === leadsClientsRange)?.label || '';
              const deltaChip = (delta) => (
                <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                  delta > 0 ? 'text-green-600 dark:text-green-400' : delta < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {delta > 0 ? '▲' : delta < 0 ? '▼' : '–'} {Math.abs(delta)}
                </span>
              );
              return (
            <div className="lg:col-span-3 lg:order-1 bg-white dark:bg-gray-900 p-6 rounded-lg shadow-sm hover-lift flex flex-col">
              <div className={`relative mb-4 ${isMobile ? 'pt-9' : ''}`}>
                <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-semibold text-center dark:text-white`}>
                  Leads &amp; Clients
                </h2>
                <select
                  value={leadsClientsRange}
                  onChange={(e) => setLeadsClientsRange(e.target.value)}
                  aria-label="Timeframe"
                  className="absolute top-0 right-0 px-3 py-1.5 pr-8 rounded-lg text-xs font-semibold border shadow-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${darkMode ? '%239CA3AF' : '%236B7280'}' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.6rem center',
                  }}
                >
                  {LEADS_CLIENTS_RANGES.map((r) => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </select>
              </div>

              {leadsClientsLoading || !leadsClientsAccounts ? (
                <div className="flex flex-col items-center justify-center w-full flex-1 min-h-[340px]">
                  <BBALoader size={40} className="mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Loading leads &amp; clients...</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="rounded-lg border border-blue-100 dark:border-blue-900/40 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/40 dark:to-gray-900 px-4 py-3">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-500" aria-hidden="true" />
                        New Leads
                      </div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">{newLeadsTotal}</span>
                        {deltaChip(leadsDelta)}
                      </div>
                      <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{rangeLabel} · vs prior period</div>
                    </div>
                    <div className="rounded-lg border border-red-100 dark:border-red-900/40 bg-gradient-to-br from-red-50 to-white dark:from-red-950/40 dark:to-gray-900 px-4 py-3">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500" aria-hidden="true" />
                        Total Clients
                      </div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">{clientsNow}</span>
                        {deltaChip(clientsDelta)}
                      </div>
                      <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Active today · change over {rangeLabel.toLowerCase()}</div>
                    </div>
                  </div>

                  <div className="w-full flex-1 min-h-[300px] rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-950/40 pt-4 pr-3 pb-1">
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                        <defs>
                          <linearGradient id="lcLeadsBar" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.95} />
                            <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.45} />
                          </linearGradient>
                          <linearGradient id="lcClientsArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#EF4444" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#EF4444" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke={gridInk} strokeDasharray="4 4" horizontal vertical />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: ink, fontSize: 11 }}
                          axisLine={{ stroke: gridInk }}
                          tickLine={false}
                          interval={leadsClientsRange === 'month' ? (isMobile ? 4 : 1) : 'preserveStartEnd'}
                          tickMargin={8}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fill: ink, fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          width={40}
                        />
                        <RechartsTooltip
                          cursor={{ stroke: gridInk, strokeWidth: 1, fill: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(17,24,39,0.03)' }}
                          labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''}
                          contentStyle={{
                            backgroundColor: surface,
                            border: `1px solid ${darkMode ? '#374151' : '#E5E7EB'}`,
                            borderRadius: 10,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            color: darkMode ? '#F9FAFB' : '#111827',
                            fontSize: 12,
                            padding: '8px 12px',
                          }}
                          labelStyle={{ color: darkMode ? '#F9FAFB' : '#111827', fontWeight: 600, marginBottom: 4 }}
                          itemStyle={{ color: darkMode ? '#E5E7EB' : '#374151', padding: 0 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10, color: ink }} iconType="circle" iconSize={8} />
                        <Bar
                          dataKey="newLeads"
                          name="New Leads"
                          fill="url(#lcLeadsBar)"
                          stroke="#3B82F6"
                          strokeWidth={1}
                          maxBarSize={leadsClientsRange === 'month' ? 14 : 32}
                          radius={[4, 4, 0, 0]}
                        />
                        <Area
                          type="monotone"
                          dataKey="totalClients"
                          name="Total Clients"
                          stroke="#EF4444"
                          strokeWidth={2.5}
                          fill="url(#lcClientsArea)"
                          connectNulls={false}
                          dot={points.length <= 14 ? { r: 4, fill: '#EF4444', stroke: surface, strokeWidth: 2 } : false}
                          activeDot={{ r: 6, fill: '#EF4444', stroke: surface, strokeWidth: 2 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Announcements Modal */}
      {showAnnouncementsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-center items-center mb-6 relative">
              <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold dark:text-white`}>
                {showNewAnnouncementForm 
                  ? 'My Announcements' 
                  : (userType === 'portal' ? 'Client Announcements' : 'Company Announcements')
                }
              </h2>
              <button
                onClick={() => {
                  setShowAnnouncementsModal(false);
                  setNewAnnouncement('');
                  setShowNewAnnouncementForm(false);
                  setSelectedFile(null);
                  setFileUploadError('');
                  setAnnouncementType(userType === 'internal' ? 'company' : 'client'); // Reset to appropriate default
                }}
                className="absolute right-0 text-gray-500 hover:text-gray-700"
              >
                <X />
              </button>
            </div>

            <div className="space-y-4">
              {/* Announcements List */}
              <div>
                {announcementsLoading ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                    Loading announcements...
                  </div>
                ) : (
                  <div className="space-y-3">
                    {announcements.map(announcement => (
                        <div 
                          key={announcement.id} 
                          className={`p-4 rounded-lg border ${
                            announcement.type === 'onboarding'
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700'
                              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${
                                announcement.type === 'onboarding'
                                  ? 'text-emerald-800 dark:text-emerald-200'
                                  : 'text-gray-800 dark:text-white'
                              }`}>
                                {announcement.message}
                              </p>
                              

                            </div>
                            <div className="flex items-center space-x-2 ml-2">
                              {!showNewAnnouncementForm && !announcement.seen && (
                                <button
                                  onClick={() => handleMarkAsSeen(announcement.id)}
                                  className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/40"
                                >
                                  Mark Seen
                                </button>
                              )}
                              {showNewAnnouncementForm && announcement.createdByUid === currentUser?.uid && (
                                <button
                                  onClick={() => handleDeleteAnnouncement(announcement.id)}
                                  className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/40 flex items-center gap-1"
                                  title="Delete announcement"
                                >
                                  <Trash2 size={12} />
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* File Attachment Display */}
                          {announcement.fileUrl && announcement.fileName && (
                            <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                              <div className="flex items-center space-x-2">
                                <span className="text-lg">{getFileIcon(announcement.fileName)}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300 truncate">
                                    {announcement.fileName}
                                  </p>
                                  {announcement.fileSize && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400">
                                      {formatFileSize(announcement.fileSize)}
                                    </p>
                                  )}
                                </div>
                                <a
                                  href={announcement.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors"
                                  title="Download file"
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                            <span>By: {announcement.createdBy}</span>
                            <span>
                              {announcement.createdAt ? 
                                new Date(announcement.createdAt).toLocaleDateString() + ' ' + 
                                new Date(announcement.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                                : 'Just now'
                              }
                            </span>
                          </div>
                        </div>
                      ))}
                    
                    {announcements.length === 0 && (
                      <div className="text-center text-gray-400 py-8">
                        No announcements available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* New Announcement Form */}
              <div className="border-t pt-4">
                {!showNewAnnouncementForm ? (
                  <button
                    onClick={() => {
                      setShowNewAnnouncementForm(true);
                      fetchMyAnnouncements(); // Fetch user's own announcements
                    }}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Make Announcement
                  </button>
                ) : (
                  <div className="space-y-3">
                    {/* Announcement Type Selection */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Announcement Type
                      </label>
                      
                      {/* Toggle Switch Design */}
                      <div className="relative">
                        <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                          <button
                            onClick={() => setAnnouncementType('company')}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                              announcementType === 'company'
                                ? 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-center space-x-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                              <span>Company</span>
                              {userType === 'internal' && (
                                <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full">
                                  Default
                                </span>
                              )}
                            </div>
                          </button>
                          
                          <button
                            onClick={() => setAnnouncementType('client')}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                              announcementType === 'client'
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-center space-x-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                              </svg>
                              <span>Client</span>
                              {userType === 'portal' && (
                                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full">
                                  Default
                                </span>
                              )}
                            </div>
                          </button>
                        </div>
                      </div>
                      
                      {/* Description */}
                      <div className={`p-3 rounded-lg border-l-4 ${
                        announcementType === 'company' 
                          ? 'bg-red-50 dark:bg-red-900/10 border-red-400 dark:border-red-600' 
                          : 'bg-blue-50 dark:bg-blue-900/10 border-blue-400 dark:border-blue-600'
                      }`}>
                        <div className="flex items-start space-x-2">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            announcementType === 'company' 
                              ? 'bg-red-100 dark:bg-red-900/30' 
                              : 'bg-blue-100 dark:bg-blue-900/30'
                          }`}>
                            <svg className={`w-3 h-3 ${
                              announcementType === 'company' 
                                ? 'text-red-600 dark:text-red-400' 
                                : 'text-blue-600 dark:text-blue-400'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <p className={`text-sm ${
                            announcementType === 'company' 
                              ? 'text-red-700 dark:text-red-300' 
                              : 'text-blue-700 dark:text-blue-300'
                          }`}>
                            {announcementType === 'company' 
                              ? 'This announcement will be visible to internal team members only (CRM side).' 
                              : 'This announcement will be visible to portal users (clients) only.'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <textarea
                      value={newAnnouncement}
                      onChange={(e) => setNewAnnouncement(e.target.value)}
                      placeholder={`Enter your ${announcementType === 'company' ? 'company' : 'client'} announcement...`}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[100px] dark:bg-gray-950 dark:text-white"
                    />
                    
                    {/* File Upload Section */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Attach File (Optional)
                      </label>
                      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                          <div className="flex justify-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="flex text-sm text-gray-600 dark:text-gray-400">
                            <label
                              htmlFor="file-upload"
                              className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-red-600 hover:text-red-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-red-500"
                            >
                              <span>Upload a file</span>
                              <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                className="sr-only"
                                onChange={handleFileSelect}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                              />
                            </label>
                            <p className="pl-1">or drag and drop</p>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            PDF, DOC, DOCX, XLS, XLSX, images, or text files up to 10MB
                          </p>
                        </div>
                      </div>
                      
                      {/* Selected File Display */}
                      {selectedFile && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">{getFileIcon(selectedFile.name)}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {selectedFile.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatFileSize(selectedFile.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={handleFileRemove}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remove file"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                      
                      {/* Error Display */}
                      {fileUploadError && (
                        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                          {fileUploadError}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-between">
                      <button
                        onClick={() => {
                          setShowNewAnnouncementForm(false);
                          setNewAnnouncement('');
                          setSelectedFile(null);
                          setFileUploadError('');
                          setAnnouncementType(userType === 'internal' ? 'company' : 'client'); // Reset to appropriate default
                        }}
                        className="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateAnnouncement}
                        disabled={!newAnnouncement.trim() || isUploading}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {isUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <span>Post Announcement</span>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task/Event Details Modal */}
      {showTaskDetailsModal && selectedTaskOrEvent && (
        <Modal isOpen={showTaskDetailsModal} onClose={() => setShowTaskDetailsModal(false)}>
          <div>
            <h2 className="text-lg font-medium w-full text-center text-gray-900 dark:text-white mb-4">
              {selectedTaskOrEvent.type === 'task' ? 'Task Details' : 'Event Details'}
            </h2>
            {taskDetailsLoading ? (
              <div className="text-center text-gray-500 dark:text-gray-400">Loading...</div>
            ) : taskDetailsError ? (
              <div className="text-center text-red-500">{taskDetailsError}</div>
            ) : (
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                  <div className="text-lg text-gray-900 dark:text-white">{selectedTaskOrEvent.title}</div>
                </div>
                {selectedTaskOrEvent.description && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <div className="text-gray-600 dark:text-gray-400">{selectedTaskOrEvent.description}</div>
                  </div>
                )}
                {selectedTaskOrEvent.type === 'task' && (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-center">Due Date</label>
                        <div className="text-center text-gray-600 dark:text-gray-400">
                          {selectedTaskOrEvent.dueDate ? convertToDate(selectedTaskOrEvent.dueDate)?.toLocaleDateString() || 'Invalid date' : 'No due date'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-center">Estimated Time</label>
                        <div className="text-center text-gray-600 dark:text-gray-400">
                          {selectedTaskOrEvent.estimatedHours || selectedTaskOrEvent.estimatedMinutes 
                            ? `${selectedTaskOrEvent.estimatedHours || 0}h ${selectedTaskOrEvent.estimatedMinutes || 0}m`
                            : 'No time estimate'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-center">Attachments</label>
                        <div className="text-center text-gray-600 dark:text-gray-400">
                          {selectedTaskOrEvent.files?.length > 0 
                            ? selectedTaskOrEvent.files.map((file, idx) => <div key={idx}>{file.name}</div>)
                            : 'No attachments'}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4 mb-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assignees</label>
                        <div className="flex flex-wrap gap-2">
                          {selectedTaskOrEvent.assignees?.map(user => (
                            <div key={user.id} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                              <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs">
                                {user.initials}
                              </div>
                              <span className="text-sm text-gray-900 dark:text-white">{user.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="w-64">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                        <div className="text-gray-600">
                          {selectedTaskOrEvent.accountId === 'internal' 
                            ? 'Internal' 
                            : accounts.find(a => a.id === selectedTaskOrEvent.accountId)?.company || 'Unknown Account'}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {selectedTaskOrEvent.type === 'event' && (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
                      <div className="text-gray-600 dark:text-gray-400">
                        {selectedTaskOrEvent.startTime ? convertToDate(selectedTaskOrEvent.startTime)?.toLocaleString() || 'Invalid date' : 'N/A'}
                      </div>
                    </div>
                    {selectedTaskOrEvent.location && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                        <div className="text-gray-600 dark:text-gray-400">
                          {selectedTaskOrEvent.location}
                        </div>
                      </div>
                    )}
                    {(selectedTaskOrEvent.isGoogleEvent || selectedTaskOrEvent.source === 'google') && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Google Calendar Event</label>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                            Google Calendar
                          </span>
                          {selectedTaskOrEvent.htmlLink && (
                            <a 
                              href={selectedTaskOrEvent.htmlLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                            >
                              View in Google Calendar
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
      </div>
    </div>
  );
};

export default Dashboard; 